import {
  EWebAnalyticsProvider,
  type EWebAnalyticsRecommendationSeverity,
  EWebAnalyticsRecommendationStatus,
  EWebAnalyticsRuleType,
  type IWebAnalyticsRecommendation,
  type IWebAnalyticsRecommendationEvidence,
  type TWebAnalyticsOperatorStatus,
} from '@mahawi/eshop-common';
import {
  Alert,
  Button,
  Descriptions,
  Popover,
  Select,
  Space,
  Table as AntTable,
  Tag,
  Typography,
} from 'antd';
import { type ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { formatMetric } from '../components/web-analytics-insights/format';
import { PROVIDER_OPTIONS } from '../components/web-analytics-insights/insights-table';
import { EDateFormat } from '../fragments/date-format';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  loadRecommendations,
  updateRecommendation,
} from '../reducers/web-analytics-insights/reducer';
import { type IWebAnalyticsRecommendationsRequest } from '../reducers/web-analytics-insights/types';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

export const RULE_TYPE_LABELS: Record<EWebAnalyticsRuleType, string> = {
  LOW_CTR: 'High impressions, low CTR',
  POSITION_DROP: 'Falling position',
  DROPPED_FROM_INDEX: 'Dropped from index',
  TRAFFIC_WITHOUT_SALES: 'Traffic without sales',
  TRAFFIC_DROP: 'Traffic drop',
};

export const SEVERITY_COLORS: Record<
  EWebAnalyticsRecommendationSeverity,
  string
> = {
  CRITICAL: 'red',
  HIGH: 'volcano',
  MEDIUM: 'gold',
  LOW: 'blue',
};

export const STATUS_COLORS: Record<EWebAnalyticsRecommendationStatus, string> =
  {
    OPEN: 'processing',
    DONE: 'success',
    DISMISSED: 'default',
    CLEARED: 'cyan',
  };

export const SUGGESTED_ACTIONS: Record<string, string> = {
  'webAnalytics.recommendation.lowCtr':
    'Rewrite the title and meta description so they match the queries this page ranks for, and check that rich results are valid.',
  'webAnalytics.recommendation.positionDrop':
    'Compare the page with the pages now ranking above it, refresh its content and check internal links pointing to it.',
  'webAnalytics.recommendation.droppedFromIndex':
    'Check the URL inspection result (canonical, noindex, robots, HTTP status), fix the cause and request indexing again.',
  'webAnalytics.recommendation.trafficWithoutSales':
    'Check price, stock, photos and the add-to-cart path on the product page; the visitors arrive but do not buy.',
  'webAnalytics.recommendation.trafficWithoutSalesUnavailable':
    'The product gets search traffic but is inactive or out of stock; restock it, reactivate it or redirect the page to an alternative.',
  'webAnalytics.recommendation.trafficDrop':
    'Look at which queries lost clicks and whether positions, impressions or CTR fell; check recent changes to the page.',
  'webAnalytics.recommendation.siteTrafficDrop':
    'Traffic fell across the whole site for this engine; check for indexing problems, outages and search engine updates.',
};

export const RECOMMENDATION_ENGINE_OPTIONS: {
  value: EWebAnalyticsProvider;
  label: string;
}[] = PROVIDER_OPTIONS.filter(
  ({ value }: { value: EWebAnalyticsProvider }): boolean =>
    value === EWebAnalyticsProvider.GOOGLE ||
    value === EWebAnalyticsProvider.BING ||
    value === EWebAnalyticsProvider.SEZNAM,
);

const PROVIDER_LABELS: Map<EWebAnalyticsProvider, string> = new Map(
  PROVIDER_OPTIONS.map(({ value, label }) => [value, label]),
);

const RULE_TYPE_OPTIONS: { value: EWebAnalyticsRuleType; label: string }[] =
  Object.values(EWebAnalyticsRuleType).map((value: EWebAnalyticsRuleType) => ({
    value,
    label: RULE_TYPE_LABELS[value],
  }));

const STATUS_OPTIONS: {
  value: EWebAnalyticsRecommendationStatus;
  label: string;
}[] = Object.values(EWebAnalyticsRecommendationStatus).map(
  (value: EWebAnalyticsRecommendationStatus) => ({ value, label: value }),
);

export const getSuggestedAction = (key: string): string =>
  Object.hasOwn(SUGGESTED_ACTIONS, key) ? SUGGESTED_ACTIONS[key] : key;

export const formatImpact = ({
  impactScore,
  impactUnit,
}: IWebAnalyticsRecommendation): string =>
  impactUnit === 'EUR'
    ? formatMetric('revenueEur', impactScore)
    : `${formatMetric('clicks', impactScore)} clicks`;

export const toListRequest = ({
  status,
  ruleType,
  provider,
  page,
  pageSize,
}: IWebAnalyticsRecommendationsRequest): IWebAnalyticsRecommendationsRequest => ({
  ...(status === undefined ? {} : { status }),
  ...(ruleType === undefined ? {} : { ruleType }),
  ...(provider === undefined ? {} : { provider }),
  page,
  pageSize,
});

const formatPeriod = (period: [string, string] | null): string =>
  period ? `${period[0]} – ${period[1]}` : EMPTY_VALUE;

export interface IEvidenceRow {
  metric: string;
  current: string;
  previous: string;
}

export const toEvidenceRows = ({
  current,
  previous,
}: IWebAnalyticsRecommendationEvidence): IEvidenceRow[] =>
  [...new Set([...Object.keys(current), ...Object.keys(previous)])].map(
    (metric: string): IEvidenceRow => ({
      metric,
      current: formatMetric(metric, current[metric] ?? null),
      previous: formatMetric(metric, previous[metric] ?? null),
    }),
  );

const evidenceColumns: ColumnType<IEvidenceRow>[] = [
  { title: 'Metric', dataIndex: 'metric', key: 'metric' },
  { title: 'Current', dataIndex: 'current', key: 'current', align: 'right' },
  { title: 'Previous', dataIndex: 'previous', key: 'previous', align: 'right' },
];

export function EvidenceContent({
  evidence,
}: {
  evidence: IWebAnalyticsRecommendationEvidence;
}): React.ReactElement {
  return (
    <Space orientation="vertical" size={SPACING.sm} style={{ maxWidth: 480 }}>
      <AntTable<IEvidenceRow>
        size="small"
        pagination={false}
        rowKey="metric"
        columns={evidenceColumns}
        dataSource={toEvidenceRows(evidence)}
      />
      <Descriptions
        size="small"
        column={1}
        items={[
          {
            key: 'periodCurrent',
            label: 'Current period',
            children: formatPeriod(evidence.periodCurrent),
          },
          {
            key: 'periodPrevious',
            label: 'Previous period',
            children: formatPeriod(evidence.periodPrevious),
          },
          ...Object.entries(evidence.thresholds).map(
            ([name, value]: [string, number]) => ({
              key: `threshold-${name}`,
              label: `Threshold ${name}`,
              children: String(value),
            }),
          ),
          ...(evidence.flags.length > 0
            ? [
                {
                  key: 'flags',
                  label: 'Flags',
                  children: evidence.flags.join(', '),
                },
              ]
            : []),
        ]}
      />
    </Space>
  );
}

export function RecommendationSubject({
  row,
}: {
  row: IWebAnalyticsRecommendation;
}): React.ReactElement {
  if (row.productUUID) {
    return (
      <Link
        to={`/product/${row.productUUID}`}
        label={row.subjectKey}
        openInNewTab
      />
    );
  }

  if (row.categoryUUID) {
    return (
      <Link
        to={`/category/${row.categoryUUID}`}
        label={row.subjectKey}
        openInNewTab
      />
    );
  }

  return <Text>{row.subjectKey}</Text>;
}

export function RecommendationActions({
  row,
  isUpdating,
  onUpdate,
}: {
  row: IWebAnalyticsRecommendation;
  isUpdating: boolean;
  onUpdate: (status: TWebAnalyticsOperatorStatus) => void;
}): React.ReactElement | null {
  if (row.status === EWebAnalyticsRecommendationStatus.OPEN) {
    return (
      <Space size={SPACING.sm}>
        <Button
          size="small"
          type="primary"
          loading={isUpdating}
          onClick={(): void => {
            onUpdate(EWebAnalyticsRecommendationStatus.DONE);
          }}
        >
          Done
        </Button>
        <Button
          size="small"
          disabled={isUpdating}
          onClick={(): void => {
            onUpdate(EWebAnalyticsRecommendationStatus.DISMISSED);
          }}
        >
          Dismiss
        </Button>
      </Space>
    );
  }

  if (
    row.status === EWebAnalyticsRecommendationStatus.DONE ||
    row.status === EWebAnalyticsRecommendationStatus.DISMISSED
  ) {
    return (
      <Button
        size="small"
        loading={isUpdating}
        onClick={(): void => {
          onUpdate(EWebAnalyticsRecommendationStatus.OPEN);
        }}
      >
        Reopen
      </Button>
    );
  }

  return null;
}

export const buildRecommendationColumns = ({
  updatingUUID,
  onUpdate,
}: {
  updatingUUID: string | undefined;
  onUpdate: (uuid: string, status: TWebAnalyticsOperatorStatus) => void;
}): ColumnType<IWebAnalyticsRecommendation>[] => [
  {
    title: 'Severity',
    dataIndex: 'severity',
    key: 'severity',
    render: (severity: EWebAnalyticsRecommendationSeverity) => (
      <Tag color={SEVERITY_COLORS[severity]}>{severity}</Tag>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'ruleType',
    key: 'ruleType',
    render: (ruleType: EWebAnalyticsRuleType): string =>
      RULE_TYPE_LABELS[ruleType] ?? ruleType,
  },
  {
    title: 'Engine',
    dataIndex: 'provider',
    key: 'provider',
    render: (provider: EWebAnalyticsProvider): string =>
      PROVIDER_LABELS.get(provider) ?? provider,
  },
  {
    title: 'Subject',
    key: 'subject',
    render: (_value: string, row: IWebAnalyticsRecommendation) => (
      <RecommendationSubject row={row} />
    ),
  },
  {
    title: 'Impact',
    key: 'impact',
    align: 'right',
    render: (_value: string, row: IWebAnalyticsRecommendation): string =>
      formatImpact(row),
  },
  {
    title: 'Suggested action',
    dataIndex: 'suggestedActionKey',
    key: 'suggestedActionKey',
    render: (key: string): string => getSuggestedAction(key),
  },
  {
    title: 'Evidence',
    dataIndex: 'evidence',
    key: 'evidence',
    render: (evidence: IWebAnalyticsRecommendationEvidence) => (
      <Popover
        title="Evidence"
        trigger="click"
        content={<EvidenceContent evidence={evidence} />}
      >
        <Button size="small" type="link">
          Show
        </Button>
      </Popover>
    ),
  },
  {
    title: 'Last detected',
    dataIndex: 'lastDetectedAt',
    key: 'lastDetectedAt',
    render: (value: string): string => dayjs(value).format(EDateFormat.DATE),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: EWebAnalyticsRecommendationStatus) => (
      <Tag color={STATUS_COLORS[status]}>{status}</Tag>
    ),
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_value: string, row: IWebAnalyticsRecommendation) => (
      <RecommendationActions
        row={row}
        isUpdating={updatingUUID === row.uuid}
        onUpdate={(status: TWebAnalyticsOperatorStatus): void => {
          onUpdate(row.uuid, status);
        }}
      />
    ),
  },
];

function WebAnalyticsRecommendationsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkUUID = searchParams.get('uuid') ?? undefined;
  const insights = useSelector((s: RootState) => s.WebAnalyticsInsights);
  const request = insights.recommendationsRequest;
  const isFiltered = deepLinkUUID !== undefined;

  const load = (next: IWebAnalyticsRecommendationsRequest): void => {
    dispatch(loadRecommendations(next));
  };

  const loadCurrent = (): void => {
    load(
      isFiltered
        ? { uuid: deepLinkUUID, page: 1, pageSize: request.pageSize }
        : toListRequest(request),
    );
  };

  useEffect((): void => {
    loadCurrent();
  }, [dispatch, deepLinkUUID]);

  const update = (
    patch: Partial<IWebAnalyticsRecommendationsRequest>,
  ): void => {
    load({ ...toListRequest(request), ...patch, page: 1 });
  };

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Web analytics recommendations"
        onReload={loadCurrent}
        isReloadDisabled={insights.recommendationsInProcess}
      />

      {isFiltered ? (
        <Alert
          type="info"
          showIcon
          title="Showing the recommendation linked from the alert email."
          action={
            <Button
              size="small"
              onClick={(): void => {
                searchParams.delete('uuid');
                setSearchParams(searchParams);
              }}
            >
              Show all
            </Button>
          }
        />
      ) : (
        <Space size={SPACING.lg} wrap>
          <Select<EWebAnalyticsRuleType[]>
            mode="multiple"
            allowClear
            placeholder="Type"
            style={{ minWidth: 240 }}
            options={RULE_TYPE_OPTIONS}
            value={request.ruleType ?? []}
            onChange={(ruleType: EWebAnalyticsRuleType[]): void => {
              update({ ruleType: ruleType.length > 0 ? ruleType : undefined });
            }}
          />
          <Select<EWebAnalyticsRecommendationStatus[]>
            mode="multiple"
            allowClear
            placeholder="Status"
            style={{ minWidth: 200 }}
            options={STATUS_OPTIONS}
            value={request.status ?? []}
            onChange={(status: EWebAnalyticsRecommendationStatus[]): void => {
              update({ status: status.length > 0 ? status : undefined });
            }}
          />
          <Select<EWebAnalyticsProvider>
            allowClear
            placeholder="Engine"
            style={{ width: 200 }}
            options={RECOMMENDATION_ENGINE_OPTIONS}
            value={request.provider}
            onChange={(provider: EWebAnalyticsProvider | undefined): void => {
              update({ provider });
            }}
          />
        </Space>
      )}

      <Table
        columns={
          buildRecommendationColumns({
            updatingUUID: insights.updatingRecommendationUUID,
            onUpdate: (
              uuid: string,
              status: TWebAnalyticsOperatorStatus,
            ): void => {
              dispatch(updateRecommendation({ uuid, status }));
            },
          }) as ColumnType<object>[]
        }
        dataSource={(insights.recommendations?.rows ?? []).map(
          (row: IWebAnalyticsRecommendation) => ({ ...row, key: row.uuid }),
        )}
        isLoading={insights.recommendationsInProcess}
        emptyText="No recommendations"
        pagination={{
          current: isFiltered ? 1 : request.page,
          pageSize: request.pageSize,
          total: insights.recommendations?.total ?? 0,
          onChange: (page: number, pageSize: number): void => {
            load({ ...toListRequest(request), page, pageSize });
          },
        }}
      />
    </Space>
  );
}

export default WebAnalyticsRecommendationsContainer;
